import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../../utils/api';
import { leaveTypeLabel, leaveBreakdownLines } from '../../utils/leaveLabels';
import { faPlus, faCalendarDay, faCheckCircle, faTimesCircle, faClock, faWallet, faTrash } from '@fortawesome/free-solid-svg-icons';
import '../../styles/App.css';

const Leaves = () => {
    const [balances, setBalances] = useState({ CL: 0, EL: 0 });
    const [leaveHistory, setLeaveHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLeaves();
    }, []);

    const fetchLeaves = async () => {
        try {
            const res = await api.get('/leaves/my-leaves');
            setLeaveHistory(res.data.history);
            setBalances(res.data.balances);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching leaves");
            setLoading(false);
        }
    };

    const handleApplyLeave = async () => {
        const TYPE_OPTIONS = [['CL', 'CL'], ['EL', 'EL'], ['UL', 'Unpaid']];
        const HALF_OPTIONS = [['FULL', 'Full Day'], ['FIRST_HALF', 'First Half'], ['SECOND_HALF', 'Second Half']];
        const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

        // Days from /leaves/plan for the chosen range, with the employee's
        // per-day choices written onto them.
        let planDays = [];
        let liveBalances = balances;
        let planSeq = 0;
        const workingDays = () => planDays.filter(d => d.kind === 'working');
        const usage = () => {
            const t = { CL: 0, EL: 0, UL: 0 };
            workingDays().forEach(d => { t[d.leaveType] += d.portion; });
            return t;
        };
        const overBalance = () => {
            const t = usage();
            return ['CL', 'EL'].filter(k => t[k] > liveBalances[k]);
        };

        const selectStyle = 'padding:4px 8px; border:1px solid light-dark(#cbd5e1, var(--theme-border-strong, #4a4e55)); border-radius:6px; font-size:13px;';
        const options = (list, selected) => list.map(([v, l]) => `<option value="${v}"${v === selected ? ' selected' : ''}>${l}</option>`).join('');

        const { value: formValues } = await Swal.fire({
            title: 'Apply for Leave',
            html: `
                <div style="text-align: left;">
                    <div style="font-size:13px; color:light-dark(#475569, var(--theme-ink-soft, #cfd3d9)); margin-bottom:8px;">
                        Available: <b>${balances.CL}</b> CL &middot; <b>${balances.EL}</b> EL
                    </div>

                    <div style="display: flex; gap: 10px;">
                        <div style="flex: 1;">
                            <label class="swal-custom-label">From Date</label>
                            <input id="date-from" type="date" class="swal2-input" style="width: 100%;">
                        </div>
                        <div style="flex: 1;">
                            <label class="swal-custom-label">To Date</label>
                            <input id="date-to" type="date" class="swal2-input" style="width: 100%;">
                        </div>
                    </div>

                    <div id="day-parts-wrap" style="display:none; margin-top: 6px;">
                        <label class="swal-custom-label">Day-wise Breakdown</label>
                        <div id="day-parts" style="border:1px solid light-dark(#e2e8f0, var(--theme-border, #33363b)); border-radius:8px; max-height:280px; overflow-y:auto;"></div>
                        <div id="days-total" style="text-align:right; font-size:13px; color:light-dark(#215D7B, var(--theme-accent-ink, #78c2e5)); margin-top:6px;"></div>
                    </div>

                    <label class="swal-custom-label">Reason</label>
                    <textarea id="leave-reason" class="swal2-textarea" placeholder="Describe reason..." style="margin: 10px auto; width: 90%;"></textarea>
                </div>
            `,
            confirmButtonText: 'Submit Request',
            confirmButtonColor: '#215D7B',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                const $ = (id) => document.getElementById(id);

                const updateTotal = () => {
                    const t = usage();
                    const total = Math.round((t.CL + t.EL + t.UL) * 100) / 100;
                    const parts = ['CL', 'EL', 'UL'].filter(k => t[k] > 0).map(k => `${t[k]} ${k}`);
                    let html = `Total: <b>${total}</b> day(s)${parts.length ? ` &middot; ${parts.join(' + ')}` : ''}`;
                    overBalance().forEach(k => {
                        html += `<br><span style="color:light-dark(#dc2626, var(--theme-danger-ink, #f19999)); font-size:12px;">Only ${liveBalances[k]} ${k} available &mdash; change ${Math.round((t[k] - liveBalances[k]) * 100) / 100} day(s) to another type.</span>`;
                    });
                    $('days-total').innerHTML = html;
                };

                const renderRows = () => {
                    const container = $('day-parts');
                    const w = workingDays();
                    const first = w[0], last = w[w.length - 1];
                    container.innerHTML = planDays.map((d, i) => {
                        const label = new Date(`${d.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
                        const rowStyle = `display:flex; align-items:center; justify-content:space-between; gap:10px; padding:8px 12px; ${i < planDays.length - 1 ? 'border-bottom:1px solid light-dark(#f1f5f9, var(--theme-border, #33363b));' : ''}`;
                        const labelHtml = `<span style="font-size:13px; color:light-dark(#334155, var(--theme-ink-soft, #cfd3d9));">${label}</span>`;
                        if (d.kind !== 'working') {
                            const why = d.kind === 'sunday' ? 'Sunday' : escapeHtml(d.holidayName || 'Holiday');
                            return `<div style="${rowStyle}">${labelHtml}<span style="font-size:12px; color:light-dark(#94a3b8, var(--theme-subtle, #7a808a));">${why} &middot; Off</span></div>`;
                        }
                        // Half days only on the first and last working day.
                        const halfId = d === first ? 'half-start' : d === last ? 'half-end' : null;
                        const halfHtml = halfId
                            ? `<select id="${halfId}" style="${selectStyle}">${options(HALF_OPTIONS, d.half)}</select>`
                            : `<span style="font-size:12px; color:light-dark(#94a3b8, var(--theme-subtle, #7a808a));">Full Day</span>`;
                        const typeHtml = `<select data-day="${d.date}" class="day-type-select" style="${selectStyle}">${options(TYPE_OPTIONS, d.leaveType)}</select>`;
                        return `<div style="${rowStyle}">${labelHtml}<span style="display:flex; align-items:center; gap:6px;">${halfHtml}${typeHtml}</span></div>`;
                    }).join('');

                    container.querySelectorAll('.day-type-select').forEach(sel => sel.addEventListener('change', () => {
                        const day = planDays.find(x => x.date === sel.dataset.day);
                        if (day) day.leaveType = sel.value;
                        updateTotal();
                    }));
                    [['half-start', first], ['half-end', last]].forEach(([id, day]) => {
                        const sel = $(id);
                        if (!sel) return;
                        sel.addEventListener('change', () => {
                            day.half = sel.value;
                            day.portion = sel.value === 'FULL' ? 1 : 0.5;
                            updateTotal();
                        });
                    });
                    updateTotal();
                };

                const loadPlan = async () => {
                    const wrap = $('day-parts-wrap');
                    const fromV = $('date-from').value, toV = $('date-to').value;
                    planDays = [];
                    const seq = ++planSeq;
                    if (!fromV || !toV || new Date(toV) < new Date(fromV)) { wrap.style.display = 'none'; return; }

                    wrap.style.display = 'block';
                    $('day-parts').innerHTML = `<div style="padding:10px 12px; font-size:13px; color:light-dark(#94a3b8, var(--theme-subtle, #7a808a));">Loading days…</div>`;
                    $('days-total').innerHTML = '';
                    try {
                        const res = await api.get('/leaves/plan', { params: { fromDate: fromV, toDate: toV } });
                        if (seq !== planSeq) return;
                        planDays = res.data.days;
                        liveBalances = res.data.balances || balances;
                        renderRows();
                    } catch (err) {
                        if (seq !== planSeq) return;
                        $('day-parts').innerHTML = `<div style="padding:10px 12px; font-size:13px; color:light-dark(#dc2626, var(--theme-danger-ink, #f19999));">${escapeHtml(err.response?.data?.message || 'Could not load the days.')}</div>`;
                    }
                };

                $('date-from').addEventListener('change', loadPlan);
                $('date-to').addEventListener('change', loadPlan);
            },
            preConfirm: () => {
                const from = document.getElementById('date-from').value;
                const to = document.getElementById('date-to').value;
                const reason = document.getElementById('leave-reason').value;

                if (!from || !to || !reason) {
                    Swal.showValidationMessage('Please fill all fields');
                    return false;
                }
                if (new Date(to) < new Date(from)) {
                    Swal.showValidationMessage('To Date cannot be before From Date');
                    return false;
                }
                const w = workingDays();
                if (!w.length) {
                    Swal.showValidationMessage(planDays.length ? 'Every selected day is a Sunday or a holiday.' : 'Wait for the days to load.');
                    return false;
                }
                const over = overBalance();
                if (over.length) {
                    Swal.showValidationMessage(`Not enough ${over.join(' and ')} balance for the days selected.`);
                    return false;
                }
                return {
                    fromDate: from,
                    toDate: to,
                    reason,
                    startHalf: w[0].half,
                    endHalf: w[w.length - 1].half,
                    breakdown: Object.fromEntries(w.map(d => [d.date, d.leaveType]))
                };
            }
        });

        if (formValues) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Submitting request and notifying HR.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                await api.post('/leaves/apply', formValues);

                Swal.fire('Submitted!', 'Your leave request has been sent and HR has been notified.', 'success');
                fetchLeaves();
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Failed to submit request.', 'error');
            }
        }
    };

    const handleDeleteLeave = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will permanently delete this leave request (testing purposes only).",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/leaves/${id}`);
                Swal.fire('Deleted!', 'Leave request has been deleted.', 'success');
                fetchLeaves();
            } catch (err) {
                Swal.fire('Error', 'Failed to delete request.', 'error');
            }
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Approved': return <span className="status-badge success"><FontAwesomeIcon icon={faCheckCircle} /> Approved</span>;
            case 'Rejected': return <span className="status-badge danger"><FontAwesomeIcon icon={faTimesCircle} /> Rejected</span>;
            default: return <span className="status-badge warning"><FontAwesomeIcon icon={faClock} /> Pending</span>;
        }
    };

    if (loading) return <div className="main-content">Loading Leaves...</div>;

    return (
        <div className="leaves-container fade-in">
            <div className="page-header-row">
                <h1 className="page-title header-no-margin">Leave Management</h1>
                <button className="action-btn-primary" onClick={handleApplyLeave}>
                    <FontAwesomeIcon icon={faPlus} className="btn-icon" /> Apply for Leave
                </button>
            </div>

            {/* --- BALANCE CARDS --- */}
            <div className="leaves-stats-grid two-cols">
                <div className="stat-card border-teal">
                    <div className="stat-icon teal-bg"><FontAwesomeIcon icon={faCalendarDay} /></div>
                    <div className="stat-info">
                        <p>Casual Leave (CL)</p>
                        <h3>{balances.CL} Available</h3>
                        <small className="text-muted text-small">+1 per month (Resets Jan 1st)</small>
                    </div>
                </div>

                <div className="stat-card border-plum">
                    <div className="stat-icon plum-bg"><FontAwesomeIcon icon={faWallet} /></div>
                    <div className="stat-info">
                        <p>Earned Leave (EL)</p>
                        <h3>{balances.EL} Available</h3>
                        <small className="text-muted text-small">Added by HR</small>
                    </div>
                </div>
            </div>

            {/* --- HISTORY TABLE --- */}
            <div className="employee-table-container mt-30 fade-in">
                <h3 className="table-header-title">Application History</h3>
                <ResponsiveTable className="employee-table">
                    <thead>
                        <tr>
                            <th>Leave Type</th>
                            <th>Dates</th>
                            <th>Days</th>
                            <th>Reason</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leaveHistory.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-table-message">No leave history found.</td>
                            </tr>
                        ) : (
                            leaveHistory.map(leave => (
                                <tr key={leave._id}>
                                    <td data-label="Leave Type" className="fw-600">
                                        {leaveTypeLabel(leave)}
                                        {leaveBreakdownLines(leave).map(line => (
                                            <div key={line} className="text-muted text-small" style={{ fontWeight: 400 }}>{line}</div>
                                        ))}
                                    </td>
                                    <td data-label="Dates" className="text-dark-gray text-small">
                                        {new Date(leave.fromDate).toLocaleDateString('en-GB')}
                                        <span className="text-muted mx-1">to</span>
                                        {new Date(leave.toDate).toLocaleDateString('en-GB')}
                                    </td>
                                    <td data-label="Days">
                                        {leave.days}
                                        {((leave.startHalf && leave.startHalf !== 'FULL') || (leave.endHalf && leave.endHalf !== 'FULL'))
                                            ? <span className="status-badge warning text-small ml-5" style={{ padding: '1px 6px', fontSize: '10px' }}>½ day</span>
                                            : ''}
                                    </td>
                                    <td data-label="Reason" className="note-cell text-muted text-small">
                                        {leave.reason}
                                    </td>
                                    <td data-label="Status">{getStatusBadge(leave.status)}</td>
                                    <td data-label="Actions">
                                        {leave.status === 'Pending' ? (
                                            <button 
                                                className="action-btn-danger" 
                                                onClick={() => handleDeleteLeave(leave._id)}
                                                style={{ padding: '5px 10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        ) : (
                                            <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>Processed</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>
        </div>
    );
};

export default Leaves;